/**
 * useAmazonMonthlyForecast
 *
 * Fetches Amazon monthly sales data + seasonality data and computes
 * the current-month forecast using the same logic as YoyMonthlyView
 * (Monthly / Amazon tab), including seasonality when enabled.
 *
 * Returns forecastSalesData keyed by SKU: { [sku]: { total_units } }
 */

import { useState, useEffect } from 'react';
import api from '../api/client';
import {
  getCurrentMonthInfo,
  getHistoricalMonths,
  getForecastMonths,
  buildMonthlySalesIndex,
  calculateMonthlyAverageGrowthFromIndex,
  calculateMonthlyForecastFromIndex,
  calculateMonthlySeasonalityForecast,
  buildMonthlyQueryRange,
} from '../utils/yoyMonthlyCalculations';

const DEFAULT_HISTORICAL_MONTHS = 2;

const getHistoricalMonthsSetting = () => {
  try {
    const v = localStorage.getItem('yoy_historical_months_count');
    return v ? Number(v) : DEFAULT_HISTORICAL_MONTHS;
  } catch { return DEFAULT_HISTORICAL_MONTHS; }
};

export const useAmazonMonthlyForecast = (products) => {
  const [forecastSalesData, setForecastSalesData] = useState({});
  const [forecastLoading, setForecastLoading]     = useState(false);

  useEffect(() => {
    if (!products || products.length === 0) return;

    const asins = products.map(p => p.ASIN).filter(Boolean);
    if (asins.length === 0) return;

    let cancelled = false;

    const run = async () => {
      setForecastLoading(true);
      try {
        const monthInfo        = getCurrentMonthInfo();
        const historicalMonths = getHistoricalMonths(monthInfo.month, monthInfo.year, getHistoricalMonthsSetting());
        const forecastMonths   = getForecastMonths(monthInfo.month, monthInfo.year, 0);

        const { startYear, startMonth, endYear, endMonth } = buildMonthlyQueryRange(
          historicalMonths, monthInfo, forecastMonths
        );

        // Fetch all 3 sources in parallel
        const [{ data: salesResult }, { data: coeffResult }, { data: flagsResult }] = await Promise.all([
          api.get(
            `/api/amazon-month-sales-range` +
            `?skus=${asins.join(',')}` +
            `&startMonth=${startMonth}&startYear=${startYear}` +
            `&endMonth=${endMonth}&endYear=${endYear}`
          ),
          api.get(`/api/seasonality-coefficients`),
          api.get(`/api/yoy-seasonality-flags?asins=${asins.join(',')}`),
        ]);

        if (cancelled || !salesResult.success) return;

        // Normalise sales data
        const salesData = {};
        Object.entries(salesResult.data).forEach(([key, value]) => {
          salesData[key] = { value: value.value ?? value.units };
        });

        // Build seasonality coeff map: { 'nicheId-month': coefficient }
        const coeffMap = {};
        if (coeffResult.success && Array.isArray(coeffResult.data)) {
          coeffResult.data.forEach(item => {
            coeffMap[`${item.niche_category_id}-${item.month_num}`] = parseFloat(item.seasonality_coefficient);
          });
        }
        const getCoeff = (nicheId, month) => coeffMap[`${nicheId}-${month}`] || 1.0;

        // Build seasonality flags map: { ASIN_UPPERCASE: true/false }
        const flagsMap = {};
        if (Array.isArray(flagsResult.data)) {
          flagsResult.data.forEach(item => {
            if (item.asin) flagsMap[item.asin.toUpperCase()] = item.seasonality_enabled === 1;
          });
        }

        // Compute current-month forecast per product (same logic as getForecastValue in YoyMonthlyView)
        const map = {};
        products.forEach(product => {
          const asin  = product.ASIN;
          const sku   = product.SKU;
          if (!asin || !sku) return;

          const index     = buildMonthlySalesIndex(asin, salesData);
          const avgGrowth = calculateMonthlyAverageGrowthFromIndex(index, historicalMonths);

          let forecast;

          // 1. Seasonality forecast (if enabled for this product)
          if (product.niche_category_id && flagsMap[asin.toUpperCase()]) {
            const seasonal = calculateMonthlySeasonalityForecast(
              asin,
              monthInfo.year,
              monthInfo.month,
              product.niche_category_id,
              index,
              getCoeff,
              monthInfo.month,
              monthInfo.year
            );
            if (seasonal !== 'N/A') forecast = seasonal;
          }

          // 2. Plain YoY growth forecast as fallback
          if (forecast === undefined) {
            const f = calculateMonthlyForecastFromIndex(index, monthInfo.year, monthInfo.month, avgGrowth);
            if (typeof f === 'number') forecast = f;
          }

          if (typeof forecast === 'number') {
            map[sku] = { total_units: forecast };
          }
        });

        setForecastSalesData(map);
      } catch (err) {
        console.error('[useAmazonMonthlyForecast] error:', err);
      } finally {
        if (!cancelled) setForecastLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [products]);

  return { forecastSalesData, forecastLoading };
};
